import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { OpportunitiesTable, type LoanRow } from "@/components/loans/OpportunitiesTable";
import { FilterChips } from "@/components/loans/FilterChips";
import { useTopbar } from "@/components/chrome/use-topbar";
import { formatUsdK, formatDate, formatTime } from "@/lib/format";
import { MarketFilterDropdown } from "@/components/markets/MarketFilterDropdown";
import { marketIdsQuery } from "@/lib/market-filter";
import { TargetFilterDropdown } from "@/components/targets/TargetFilterDropdown";
import type { TargetStatus } from "@/lib/targets-schemas";
import { meProfileQueryOptions } from "@/lib/me-profile";
import { useToast } from "@/components/ui/Toast";

interface Run {
  id: string;
  startedAt: number;
  finishedAt: number | null;
  status: "running" | "ok" | "partial" | "failed";
  trigger: "cron" | "manual";
  triggeredByEmail: string | null;
  lookbackDays: number;
  newCandidates: number;
}

type Filter = "all" | "A" | "B" | "awaiting_mail" | "mailed";

type SortKey =
  | "delta"
  | "loan"
  | "arv"
  | "borrower"
  | "lender"
  | "property"
  | "status"
  | "priority"
  | "date";

interface OppsSearch {
  q: string;
  sort: SortKey;
  order: "asc" | "desc";
  page: number;
  filter: Filter;
}

const SORT_LABELS: Record<SortKey, string> = {
  delta: "Dynamo Δ",
  loan: "Loan",
  arv: "ARV",
  borrower: "Borrower",
  lender: "Current lender",
  property: "Property",
  status: "Status",
  priority: "Tier",
  date: "Date of loan",
};

export const Route = createFileRoute("/_app/opportunities")({
  validateSearch: (s: Record<string, unknown>): OppsSearch => ({
    q: typeof s.q === "string" ? s.q : "",
    sort: (typeof s.sort === "string" ? s.sort : "delta") as SortKey,
    order: s.order === "asc" ? "asc" : "desc",
    page: Math.max(Number(s.page ?? 1), 1),
    filter: (typeof s.filter === "string" ? s.filter : "all") as Filter,
  }),
  component: OpportunitiesPage,
});

function OpportunitiesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: profile } = useQuery(meProfileQueryOptions);
  const marketIds = profile?.opportunitiesMarketIds ?? null;
  const [excludedTargets, setExcludedTargets] = useState<TargetStatus[]>(["won", "lost", "passed"]);

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (patch: Partial<OppsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });

  // Local immediate text state for the input; debounced into the URL.
  const [searchInput, setSearchInput] = useState(search.q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search.q) setSearch({ q: searchInput, page: 1 });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const PAGE_SIZE = 50;
  const statusParam =
    search.filter === "awaiting_mail"
      ? "awaiting_mail"
      : search.filter === "mailed"
        ? "mailed"
        : "awaiting_mail,mailed,skipped";
  const priorityParam = search.filter === "A" ? "A" : search.filter === "B" ? "B" : "";

  const listUrl =
    `/api/loans?status=${statusParam}` +
    (priorityParam ? `&priority=${priorityParam}` : "") +
    `&sort=${search.sort}&order=${search.order}&page=${search.page}&pageSize=${PAGE_SIZE}` +
    (search.q ? `&q=${encodeURIComponent(search.q)}` : "") +
    marketIdsQuery(marketIds) +
    (excludedTargets.length ? `&excludeTargetStatus=${excludedTargets.join(",")}` : "");

  const { data: loansData, isLoading } = useQuery<{ rows: LoanRow[]; total: number }>({
    queryKey: ["loans", "today", listUrl],
    queryFn: () => apiFetch<{ rows: LoanRow[]; total: number }>(listUrl),
  });

  const summaryUrl =
    `/api/loans/summary?` +
    (search.q ? `q=${encodeURIComponent(search.q)}&` : "") +
    marketIdsQuery(marketIds).replace(/^&/, "") +
    (excludedTargets.length ? `&excludeTargetStatus=${excludedTargets.join(",")}` : "");

  const { data: summary } = useQuery<{
    total: number;
    byPriority: Record<"A" | "B" | "skip", number>;
    byStatus: Record<"new" | "awaiting_mail" | "mailed" | "skipped" | "stale", number>;
    totalDeltaUsd: number;
    avgDeltaUsd: number;
    topLendersA: { lender: string; count: number }[];
    topLendersB: { lender: string; count: number }[];
  }>({
    queryKey: ["loans", "summary", summaryUrl],
    queryFn: () => apiFetch(summaryUrl),
  });
  const { data: runsData } = useQuery<{ rows: Run[] }>({
    queryKey: ["runs", "recent", marketIds],
    queryFn: () => apiFetch<{ rows: Run[] }>(`/api/runs?limit=7${marketIdsQuery(marketIds)}`),
  });

  const markMailed = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/loans/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: "mailed" }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["loans"] }),
  });

  const bulkMarkMailed = useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch("/api/loans/bulk-status", {
        method: "POST",
        body: JSON.stringify({ ids, status: "mailed" }),
      }),
    onSuccess: (_data, ids) => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      setSelectedIds(new Set());
      toast.success(`Marked ${ids.length} as mailed`);
    },
  });

  const reRunScan = useMutation({
    mutationFn: () => apiFetch("/api/runs/trigger", { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["runs"] });
      toast.success("Scan started");
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        toast.error("A run is already in progress");
      } else {
        toast.error("Could not start scan");
      }
    },
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const rows = useMemo(() => loansData?.rows ?? [], [loansData]);

  // Recover from an out-of-range page: filters that don't reset page (target
  // exclusion, market profile, a shared ?page=N URL) can leave the offset past
  // the end of a smaller result set — the API returns no rows with total > 0,
  // which would otherwise strand the user on an empty page with no pager.
  useEffect(() => {
    if (loansData && loansData.rows.length === 0 && loansData.total > 0 && search.page > 1) {
      // Replace (not push) so Back doesn't re-enter the bad ?page=N and re-trigger this.
      void navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true });
    }
  }, [loansData, search.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const latestRun = runsData?.rows?.[0];

  const counts: Record<Filter, number> = {
    all: summary?.total ?? 0,
    A: summary?.byPriority.A ?? 0,
    B: summary?.byPriority.B ?? 0,
    awaiting_mail: summary?.byStatus.awaiting_mail ?? 0,
    mailed: summary?.byStatus.mailed ?? 0,
  };
  const skippedCount = summary?.byStatus.skipped ?? 0;
  const totalDelta = summary?.totalDeltaUsd ?? 0;
  const avgDelta = summary?.avgDeltaUsd ?? 0;

  const formatLenders = (list?: { lender: string; count: number }[]) =>
    list?.map((l) => `${l.lender} (${l.count})`).join(" · ") ?? null;

  const sevenDayAvg =
    runsData && runsData.rows.length > 1
      ? Math.round(
          runsData.rows.slice(1).reduce((s, r) => s + r.newCandidates, 0) /
            (runsData.rows.length - 1),
        )
      : null;
  const reUnderwrittenDelta =
    sevenDayAvg !== null && latestRun ? latestRun.newCandidates - sevenDayAvg : null;

  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(rows.map((r) => r.id)) : new Set());

  const onSort = (key: SortKey) =>
    setSearch(
      key === search.sort
        ? { order: search.order === "asc" ? "desc" : "asc", page: 1 }
        : { sort: key, order: "desc", page: 1 },
    );

  const handleExportCsv = async () => {
    const exportUrl =
      `/api/loans?status=${statusParam}` +
      (priorityParam ? `&priority=${priorityParam}` : "") +
      `&export=1` +
      (search.q ? `&q=${encodeURIComponent(search.q)}` : "") +
      marketIdsQuery(marketIds) +
      (excludedTargets.length ? `&excludeTargetStatus=${excludedTargets.join(",")}` : "");
    const { rows: allRows } = await apiFetch<{ rows: LoanRow[] }>(exportUrl);
    const csv = rowsToCsv(allRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `opportunities-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const subtitle = latestRun
    ? `${new Date(latestRun.startedAt).toLocaleDateString("en-US", { weekday: "long" })} · last ${latestRun.lookbackDays} days · ${latestRun.newCandidates} re-underwritten · ${counts.awaiting_mail} awaiting mail · ${counts.mailed} mailed · ${skippedCount} skipped`
    : `${counts.awaiting_mail} awaiting mail · ${counts.mailed} mailed · ${skippedCount} skipped`;

  const { setTopbar } = useTopbar();
  const syncAt = latestRun?.finishedAt ?? null;
  const isReRunning = reRunScan.isPending;
  const reRunScanMutate = reRunScan.mutate;
  useEffect(() => {
    setTopbar({
      crumbs: [{ label: "Operations" }, { label: "Opportunities" }],
      rightSlot: (
        <>
          {syncAt ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-pos-bg px-3 py-1.5 text-[11px] font-bold text-pos">
              <span className="h-1.5 w-1.5 rounded-full bg-pos" />
              Scan complete · {formatDate(syncAt)} · {formatTime(syncAt)} MT
            </span>
          ) : null}
          <button
            type="button"
            disabled={isReRunning}
            onClick={() => reRunScanMutate()}
            className="inline-flex items-center gap-2 rounded-md border border-rule-strong bg-surface px-3.5 py-2 text-xs font-semibold text-ink disabled:opacity-60"
          >
            {isReRunning ? (
              <>
                <svg
                  className="h-3.5 w-3.5 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden
                >
                  <path d="M12 3a9 9 0 1 0 9 9" strokeLinecap="round" />
                </svg>
                Scanning…
              </>
            ) : (
              "Re-run scan"
            )}
          </button>
        </>
      ),
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar, syncAt, isReRunning, reRunScanMutate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-rule pb-5 md:flex-row md:items-end md:justify-between md:gap-6">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Opportunities</h1>
          <p className="text-sm text-muted">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <MarketFilterDropdown />
          <TargetFilterDropdown
            excluded={excludedTargets}
            onChange={(next) => {
              setExcludedTargets(next);
              setSearch({ page: 1 });
            }}
          />
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={(loansData?.total ?? 0) === 0}
            title="Export the current view to CSV"
            className="inline-flex items-center gap-2 rounded-md border border-rule-strong bg-surface px-3.5 py-2 text-xs font-semibold text-ink hover:bg-paper-2 disabled:opacity-60"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded border border-rule bg-rule md:grid-cols-4">
        <StatCell
          label="Re-underwritten in current run"
          value={(latestRun?.newCandidates ?? counts.all).toString()}
          sub={
            reUnderwrittenDelta != null
              ? `${reUnderwrittenDelta >= 0 ? "+" : ""}${reUnderwrittenDelta} vs 7-day avg`
              : null
          }
          subTone={reUnderwrittenDelta != null && reUnderwrittenDelta >= 0 ? "pos" : "muted"}
        />
        <StatCell
          tone="pos"
          label="Priority A · whitelist"
          value={counts.A.toString()}
          sub={formatLenders(summary?.topLendersA)}
        />
        <StatCell
          tone="info"
          label="Priority B · open net"
          value={counts.B.toString()}
          sub={formatLenders(summary?.topLendersB)}
        />
        <StatCell
          accent
          label="Total dollar Δ surfaced"
          value={formatUsdK(totalDelta).replace("$", "$")}
          sub={`avg ${formatUsdK(avgDelta)} per deal`}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <FilterChips
          value={search.filter}
          onChange={(f) => {
            setSearch({ filter: f, page: 1 });
            setSelectedIds(new Set());
          }}
          counts={counts}
        />
        <div className="flex w-full items-center gap-2 rounded-md border border-rule bg-surface px-3 py-2 md:max-w-[280px] md:flex-1">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-muted-2"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Borrower, property, lender…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 border-0 bg-transparent text-sm text-ink placeholder:text-muted-2 outline-none"
          />
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted">
          {selectedIds.size > 0 ? (
            <span className="inline-flex items-center gap-3 rounded-md bg-ink py-1 pl-3.5 pr-1 text-xs font-semibold text-surface">
              <span>{selectedIds.size} selected</span>
              <button
                type="button"
                onClick={() => {
                  const ids = Array.from(selectedIds);
                  if (window.confirm(`Mark ${ids.length} as mailed?`)) {
                    bulkMarkMailed.mutate(ids);
                  }
                }}
                className="rounded bg-tan-light px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-ink"
              >
                Mark mailed
              </button>
            </span>
          ) : null}
          <span>
            Sorted by{" "}
            <strong className="font-semibold text-ink">
              {SORT_LABELS[search.sort]} {search.order === "asc" ? "↑" : "↓"}
            </strong>
          </span>
        </div>
      </div>

      {/* Queue table */}
      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-rule-strong bg-paper-2 px-6 py-12 text-center">
          <span className="text-2xl text-muted-2" aria-hidden>
            ⌕
          </span>
          <p className="text-sm font-medium text-ink">No loans match this filter</p>
          <p className="text-xs text-muted">
            Try clearing the search or selecting a different tier.
          </p>
        </div>
      ) : (
        <OpportunitiesTable
          rows={rows}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          onMarkMailed={(id) => markMailed.mutate(id)}
          sortKey={search.sort}
          sortDir={search.order}
          onSort={onSort}
          page={search.page}
          pageSize={PAGE_SIZE}
          total={loansData?.total ?? 0}
          onPage={(p) => setSearch({ page: p })}
        />
      )}

      {/* Footnote */}
      <p className="mt-2 border-l-2 border-tan-dark bg-paper-2 px-5 py-3.5 text-xs leading-relaxed text-muted">
        <strong className="text-ink">Lookback:</strong> {latestRun?.lookbackDays ?? "—"}-day window
        ·{" "}
        <a href="/settings/schedule" className="text-ink underline underline-offset-2">
          change cadence
        </a>
      </p>
    </div>
  );
}

const CSV_COLUMNS: { header: string; get: (r: LoanRow) => string | number | null }[] = [
  { header: "Borrower", get: (r) => r.borrowerName },
  { header: "Principal", get: (r) => r.principalName },
  { header: "Lender", get: (r) => r.lender },
  { header: "Street", get: (r) => r.street },
  { header: "City", get: (r) => r.city },
  { header: "State", get: (r) => r.state },
  { header: "ZIP", get: (r) => r.zip },
  { header: "Loan Amount", get: (r) => r.loanAmountUsd },
  { header: "ARV", get: (r) => r.arvUsd },
  { header: "Dynamo Max", get: (r) => r.dynamoMaxUsd },
  { header: "Dynamo Delta", get: (r) => r.deltaUsd },
  { header: "Priority", get: (r) => r.priority },
  { header: "Status", get: (r) => r.status },
  { header: "Prior Loans", get: (r) => r.priorLoansCount },
  { header: "Phone", get: (r) => r.primaryPhone },
  { header: "Email", get: (r) => r.primaryEmail },
  { header: "Negative Remarks", get: (r) => (r.hasNegativeRemarks ? "yes" : "no") },
];

function csvCell(v: string | number | null): string {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rowsToCsv(rows: LoanRow[]): string {
  const head = CSV_COLUMNS.map((c) => c.header).join(",");
  const body = rows.map((r) => CSV_COLUMNS.map((c) => csvCell(c.get(r))).join(",")).join("\n");
  return `${head}\n${body}\n`;
}

interface StatProps {
  label: string;
  value: string;
  sub?: string | null;
  subTone?: "pos" | "muted";
  tone?: "pos" | "info";
  accent?: boolean;
}
function StatCell({ label, value, sub, subTone = "muted", tone, accent }: StatProps) {
  const cellBg = accent
    ? "bg-gradient-to-br from-[#1a1a1a] to-ink text-surface"
    : tone === "pos"
      ? "bg-pos-bg"
      : tone === "info"
        ? "bg-blue-50"
        : "bg-surface";
  const valueColor = accent
    ? "text-surface"
    : tone === "pos"
      ? "text-pos"
      : tone === "info"
        ? "text-blue-800"
        : "text-ink";
  return (
    <div className={`flex flex-col gap-1.5 px-5 py-4 ${cellBg}`}>
      <span
        className={`text-[10px] font-semibold uppercase tracking-wider ${
          accent ? "text-surface/55" : "text-muted-2"
        }`}
      >
        {label}
      </span>
      <span className={`text-3xl font-semibold tracking-tight ${valueColor}`}>{value}</span>
      {sub ? (
        <span
          className={`text-[11px] ${
            accent ? "text-tan-light" : subTone === "pos" ? "font-semibold text-pos" : "text-muted"
          }`}
        >
          {sub}
        </span>
      ) : null}
    </div>
  );
}
